/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useCallback
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { getAuthUser } from '@pepper/utils/src/api/auth/auth'
import { useShowMessage } from '../providers'
import { dataLayerLogoutEvent } from '@pepper/utils/src/function/dataLayerEvents'
import { apm } from '@elastic/apm-rum'

type AuthContextType = {
  user: User
  getAuthUser: () => Promise<void>
  refetchUser: () => Promise<void>
  logout: () => void
}

type ReducerType =
  | 'GET_AUTH_USER_SUCCESS'
  | 'GET_AUTH_USER_REQUEST'
  | 'GET_AUTH_USER_FAILURE'
  | 'LOGOUT_REQUEST'
  | 'LOGOUT_SUCCESS'

const initialState = {
  authUser: {
    avatar: null,
    company_name: null,
    company_details: [],
    email: '',
    first_name: null,
    full_name: null,
    last_name: null,
    middle_name: null,
    phone: null,
    phone_ext: null,
    profile: {
      availability_status: null,
      onboarding_status: null,
      company_setup_done: false,
      is_profile_filled: false
    },
    role: null!,
    uid: '',
    user_code: '',
    user_timezone: null,
    companies: []
  }
}

// default values are added in the component
const AuthContext = createContext<AuthContextType>({
  user: {
    ...initialState.authUser
  },
  getAuthUser: async () => {},
  refetchUser: async () => {},
  logout: () => {}
})

const reducer = (state: any, action: { type: ReducerType; payload?: User }) => {
  switch (action.type) {
    case 'GET_AUTH_USER_SUCCESS':
      // adding email & client type to data layer
      window.dataLayer?.push({
        email: action.payload?.email,
        name: action.payload?.full_name,
        firstName: action.payload?.first_name ?? undefined,
        lastName: action.payload?.last_name ?? undefined,
        role: action.payload?.role,
        phone: action.payload?.phone
          ? `${action.payload?.phone_ext ?? ''}${action.payload?.phone}`
          : null,
        userId: action.payload?.uid,
        status: action.payload?.profile.onboarding_status,
        availability_status: action.payload?.profile.availability_status
      })
      // send `user-active` event on GTM on successful authentication
      window.dataLayer?.push({
        event: 'user-active'
      })

      if (action.payload) {
        apm.setUserContext({
          id: action.payload.uid,
          username: action.payload.full_name ?? '',
          email: action.payload.email
        })
      }

      return {
        authUser: action.payload
      }
    case 'GET_AUTH_USER_REQUEST':
    case 'GET_AUTH_USER_FAILURE':
      return state
    case 'LOGOUT_REQUEST':
      return state
    case 'LOGOUT_SUCCESS': {
      dataLayerLogoutEvent()
      return initialState
    }
    default:
      return state
  }
}

const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const showMessage = useShowMessage()
  const queryClient = useQueryClient()

  const handleGetUser = useCallback(async () => {
    const { status, data, error } = await getAuthUser()
    if (status === 'ok') {
      dispatch({ type: 'GET_AUTH_USER_SUCCESS', payload: data ?? undefined })
    } else {
      showMessage({ type: 'error', message: error })
      dispatch({ type: 'GET_AUTH_USER_FAILURE' })
    }
  }, [showMessage])

  useEffect(() => {
    handleGetUser()
  }, [])

  const logout = (): void => {
    dispatch({ type: 'LOGOUT_REQUEST' })
    try {
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      queryClient.clear()
      dispatch({ type: 'LOGOUT_SUCCESS' })
    } catch (error) {
      showMessage(error.message || `Sorry. We couldn't log you out.`)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.authUser,
        getAuthUser: handleGetUser,
        refetchUser: handleGetUser,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// hooks for accessing context
const useAuthState = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useStateConstant must be used within a Auth')
  }
  return context
}

export { AuthProvider, useAuthState }
